import { useContext, useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ButtonLoader } from "../ButtonLoader";
import { signInWithFirebaseCustomToken, singIn } from "../../firebase/firebase";
import { signInWithPracticalReadingAccess } from "../../services/practicalReadingAccess.service";
import { AppContext } from "../../Context";

const MySwal = withReactContent(Swal);

function FormLogin() {
  const { showGlobalLoader, hideGlobalLoader } = useContext(AppContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSubmitLoaderButton, setShowSubmitLoaderButton] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const showInvalidCredentialsAlert = async () => {
    await MySwal.fire({
      icon: "error",
      title: "Datos incorrectos",
      text: "Correo o contraseña incorrectos. Corrige la información para ingresar.",
      confirmButtonText: "Entendido",
    });
  };

  const shouldTryPracticalReadingFallback = (error) => {
    const errorCode = String(error?.code || "");

    return [
      "auth/invalid-credential",
      "auth/invalid-login-credentials",
      "auth/user-not-found",
      "auth/wrong-password",
    ].includes(errorCode);
  };

  const handlePracticalReadingFallback = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const result = await signInWithPracticalReadingAccess({ email: normalizedEmail, password });

    if (result?.status === "authenticated" && result?.customToken) {
      await signInWithFirebaseCustomToken(result.customToken);
      return;
    }

    if (result?.status === "pending_assignment") {
      return result;
    }

    throw new Error("No se recibió una respuesta válida de autenticación.");
  };

  const handlerOnSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      MySwal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor completa todos los campos",
      });
      return;
    }

    let loaderVisible = false;
    const stopLoading = () => {
      if (!loaderVisible) return;
      hideGlobalLoader();
      loaderVisible = false;
    };

    try {
      setShowSubmitLoaderButton(true);
      showGlobalLoader();
      loaderVisible = true;
      setEmail(normalizedEmail);
      await singIn(normalizedEmail, password);
    } catch (err) {
      console.error("Error on FormLogin.handlerOnSubmit", err);

      if (!shouldTryPracticalReadingFallback(err)) {
        stopLoading();
        await showInvalidCredentialsAlert();
        return;
      }

      try {
        const fallbackResult = await handlePracticalReadingFallback();

        if (fallbackResult?.status === "pending_assignment") {
          stopLoading();
          await MySwal.fire({
            icon: "info",
            title: "Acceso en validación",
            text:
              fallbackResult.message ||
              "Tu acceso quedó en espera de validación por parte de tu psicopedagogo.",
            confirmButtonText: "Entendido",
          });
        }
      } catch (practicalReadingError) {
        console.error("Error on FormLogin.handlePracticalReadingFallback", practicalReadingError);
        stopLoading();

        if (practicalReadingError?.code === "functions/unauthenticated") {
          await showInvalidCredentialsAlert();
          return;
        }

        await MySwal.fire({
          icon: "error",
          title: "No se pudo validar el acceso",
          text:
            practicalReadingError?.message ||
            "Verifica tus credenciales e inténtalo de nuevo.",
          confirmButtonText: "Entendido",
        });
      }
    } finally {
      stopLoading();
      setShowSubmitLoaderButton(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handlerOnSubmit(e);
    }
  };

  return (
    <div>
      <div className="card-body">
        <form onSubmit={handlerOnSubmit}>
          <div className="mb-3">
            <div htmlFor="exampleInputEmail1" className="form-label fs-xs fw-bold">
              Correo electrónico
            </div>
            <input
              type="email"
              className="form-control"
              id="exampleInputEmail1"
              aria-describedby="emailHelp"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmail((currentEmail) => currentEmail.trim().toLowerCase())}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="mb-3">
            <div htmlFor="exampleInputPassword1" className="form-label fs-xs fw-bold">
              Contraseña
            </div>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                id="exampleInputPassword1"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <span
                className="input-group-text"
                onClick={handleTogglePasswordVisibility}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleTogglePasswordVisibility();
                  }
                }}
              >
                {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
              </span>
            </div>
          </div>
          {showSubmitLoaderButton ? (
            <ButtonLoader />
          ) : (
            <button type="submit" className="btn btn-primary btn-full">
              Ingresar
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export { FormLogin };
